import * as React from 'react';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { sum, cloneDeep } from 'lodash';
import { date2str } from 'utils/date';
import { CompanyDetailedDto } from 'models/dtoTypes';
import Table from '@mui/material/Table';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import DartChart from 'components/DartChart';
import ScoreUpdateWidget from 'components/ScoreUpdateWidget';
const PREFIX = 'AdminCompanyTable';

const classes = {
  root: `${PREFIX}-root`,
  badge: `${PREFIX}-badge`,
  table: `${PREFIX}-table`,
  tableBody: `${PREFIX}-tableBody`,
  tableRow: `${PREFIX}-tableRow`,
  score: `${PREFIX}-score`,
  external: `${PREFIX}-external`,
  resultRow: `${PREFIX}-resultRow`
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',

  [`& .${classes.badge}`]: {
    width: '100%',
    height: 50,
    backgroundColor: theme.palette.primary.main,
    fontSize: 30,
    paddingLeft: 15,
    lineHeight: '50px',
    color: 'white'
  },

  [`& .${classes.table}`]: {
    padding: 0,
    borderCollapse: 'separate',
    borderSpacing: '0px 20px'
  },

  [`& .${classes.tableBody}`]: {
    padding: theme.spacing(6),
  },

  [`& .${classes.tableRow}`]: {
    marginBottom: theme.spacing(3),
    border: '1px solid',
    fontSize: 16,
    borderRadius: 5,
    display: 'flex'
  },

  [`& .${classes.score}`]: {
    backgroundColor: '#24c8a8',
    color: 'white',
    fontWeight: 'bold',
    width: 100,
    padding: 0,
    '&.dissolved': {
      backgroundColor: '#707071'
    }
  },

  [`& .${classes.resultRow}`]: {
    cursor: 'pointer',
    '&:hover': {
      background: '#f1f1f1',
    },
    '&.dissolved, &.dissolved:hover': {
      cursor: 'auto',
      backgroundColor: '#bfc0c2',
    },
  },

  [`& .${classes.external}`]: {
    paddingLeft: theme.spacing(2)
  }
}));

// import ScoreUpdateWidget from 'components/ScoreUpdateWidget';
const columns: Column[] = [
  {
    label: 'Company Details',
    width: 3,
    render: (company) => {
      const { id = '', name, companyNumber, userId, sourceCompanyId, registrationNumbers } = company;
      const { address } = company;
      if (!address) {
        return 'Unknown';
      }
      const { addressLine1, addressLine2, city, country, postalCode } = address;
      const AddrComponents = [addressLine1, addressLine2, city, country, postalCode];
      return (
        <div>
          <div>
            Company Name: {name}
          </div>
          <div>
            Company Reg: {companyNumber}
          </div>
          <div>
            User Identity: {userId}
          </div>
          <div>
            Company Identity: {id}
          </div>
          <div>
            Source Company Id: {sourceCompanyId}
          </div>

          {registrationNumbers && !!registrationNumbers.length && <div>
            External Identifiers:<br />{registrationNumbers?.map((item) => <div className={classes.external} key={item.identifier}>{item.typeDescription}: {item.identifier} <br/></div> )}
          </div>}
        
          <div>
            Address: {AddrComponents.filter(c => c).join(', ')}
          </div>
        </div>
      );
    }
  },
  {
    label: 'Onboarding status',
    width: 1,
    dataIndex: 'onboardingStatus',
  },
  {
    label: 'Last Updated',
    width: 1,
    render: (company) => {
      const { lastUpdate } = company;
      return lastUpdate ? date2str(lastUpdate, 'YY-MM-DD HH:mm') : 'Never scored';
    }
  },
  {
    label: 'Status',
    width: 1,
    dataIndex: 'statusInfo'
  },
  {
    label: 'Sustainability Score',
    width: 1,
    dataIndex: 'score'
  },
  {
    label: 'Action',
    width: 1,
    // implement in CompanyItem
    render: (company, onTrigger, onComplete) => {
      const { statusInfo, scoringState, id, score } = company;

      const isDissolved = statusInfo === 'Dissolved';
      return (
        <ScoreUpdateWidget
          initialScore={score || 0} // Not in use, shold only show InProgress/NotStarted state
          initialState={scoringState || ''}
          allowRestart={true}// always show trigger button as initial state
          companyId={id}
          disabled={isDissolved}
          onComplete={onComplete}
          onTrigger={onTrigger}
        />
      );
    }
  }
];

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    color: 'black',
    fontWeight: 'bold',
    border: 'none',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
    // borderRadius: 5,
    borderTop: '1px solid',
    borderBottom: '1px solid',
    '&:first-of-type': {
      borderLeft: '1px solid',
      borderTopLeftRadius: 5,
      borderBottomLeftRadius: 5
    },
    '&:last-child': {
      borderRight: '1px solid',
      borderTopRightRadius: 5,
      borderBottomRightRadius: 5,
      // transform: 'scale(1.05)',
    }
  },
}));

const GraphTableCell = styled(TableCell)(() => ({
  border: 'none',
  padding: 0
}));

interface Props {
  companies: CompanyDetailedDto[];
  onOrder?(field: string): void;
}

interface OnComplete {
  (company: CompanyDetailedDto): void;
}

interface OnTrigger {
  (id: string): void;
}

interface CompanyItemProps {
  company: CompanyDetailedDto;
  disabled?: boolean;
  onComplete: OnComplete;
  onTrigger: OnTrigger;
}

interface Render {
  (company: CompanyDetailedDto, onTrigger?: OnTrigger, onComplete?: OnComplete): string | JSX.Element;
}
interface Column {
  label: string;
  width?: number;
  dataIndex?: string;
  render?: Render;
}
function CompanyItem (props: CompanyItemProps): JSX.Element {

  const [open, setOpen] = React.useState(false);
  const {
    onTrigger,
    onComplete,
  } = props;
  const [company, setCompany] = React.useState<CompanyDetailedDto>(props.company);
  const { statusInfo, score, scores } = company;

  const isDissolved = statusInfo === 'Dissolved';

  const handleToggle = () => {
    if (isDissolved) {
      return;
    }
    setOpen(!open);
  };

  const handleComplete = (company: CompanyDetailedDto) => {
    setCompany(company);
    onComplete(company);
  };

  const handleTrigger = (id: string) => {
    const newCompany = cloneDeep(company);
    newCompany.scoringState = 'InProgress';
    setCompany(newCompany);
    onTrigger(id);
  };

  return (
    <>
      <TableRow onClick={handleToggle} className={clsx(classes.resultRow, isDissolved ? 'dissolved' : null)}>
        {columns.map((column, i) => {
          const { render, dataIndex } = column;
          const text = dataIndex ? company[dataIndex] : '';
          return (
            <StyledTableCell key={i}>
              {render && render(company, handleTrigger, handleComplete) || text}
            </StyledTableCell>
          );
        })}
      </TableRow>
      <TableRow>
        <GraphTableCell colSpan={columns.length || 1}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box>
              <DartChart
                overallScore={score ?? 0}
                scores={scores ?? []}
              />
            </Box>
          </Collapse>
        </GraphTableCell>
      </TableRow>
    </>
  );
}

export default function AdminCompanyTable(props: Props): JSX.Element {
  const MAX_POLL = 30;

  const companies = props.companies;
  const triggered: string[] = (companies || [])
    .filter(c => c.id && c.scoringState === 'InProgress' && c.statusInfo !== 'Dissolved')
    .map(c => c.id || '');
  const [idlist, setIdlist] = React.useState<string[]>(triggered);

  const idlistRef = React.useRef(idlist);
  idlistRef.current = idlist;

  // update polling results
  const handleCompleteCompany = (company: CompanyDetailedDto) => {
    removeIdFromList(company.id || '');
  };

  // this method is passed into children and will be used in setTimeout, so have to use a Ref container to fetch lastest idlist
  const removeIdFromList = (id: string) => {
    const list = idlistRef.current;
    const idx = list.indexOf(id);
    if (idx >= 0) {
      const newList = [...list];
      newList.splice(idx, 1);
      setIdlist(newList);
    }
  };

  // what ids are triggered
  const handleTrigger = (id: string) => {
    if (!idlist.includes(id)) {
      setIdlist([...idlist, id]);
    }
  };

  const sumWidth = sum(columns.map(c => c.width || 1));
  const headers = columns.map(c => ({
    label: c.label,
    width: `${(c.width || 1) / sumWidth * 100}%`
  }));

  return (
    <Root>
      <div className={classes.badge}>Results</div>
      <Table className={classes.table} aria-label="company result table">
        <TableHead>
          <TableRow>
            {headers.map((h, i) =>
              <StyledTableCell key={i} style={{width: h.width}}>{h.label}</StyledTableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {companies.map((c, i) => (
            <CompanyItem
              company={c}
              key={i}
              disabled={idlist.length >= MAX_POLL}
              onComplete={handleCompleteCompany}
              onTrigger={handleTrigger}
            />
          ))}
        </TableBody>
      </Table>
    </Root>
  );
}
